import { useState } from 'react';
import { Segment, Header, Form, Button, Message, Container } from "semantic-ui-react";
import { Helmet } from "react-helmet";
import SIcon from "views/NUI/StreamlineIcons/SIcon";

import user_model from "models/user_model";
import { PasswordChecklist } from "./Account/PasswordChecklist";
import { useEncryptString } from "@iscr/xAppLib/Hooks/useEncryptString";
import { useAsyncUpdate } from "@iscr/xAppLib/Hooks/useAsync";
import { pick } from "@iscr/helpers/json_functions";

const Password = () => {
	user_model.limit_access("user");
	const [passwordVisible, setPasswordVisible] = useState(false);
	const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
	const { encryptString } = useEncryptString();

	const [state, setState] = useState({
		password: "",
		current_password: "",
	});

	const onChange = (e, { name, value }) => {
		setState(prev => ({
			...prev,
			[name]: value,
			error: false,
			success: false,
		}));
	};

	const {
		loading,
		error,
		data: success,
		fn: updatePassword,
	} = useAsyncUpdate({
		fn: async ({ password, current_password }) => {
			try {
				// if (!isValidPassword(password)) {
				// 	return Promise.reject("The new password is not strong enough!");
				// }
				const { encryptedWithKeyName } = await encryptString(password);

				if (!encryptedWithKeyName) {
					return Promise.reject("Failed to update password");
				}

				await user_model.frb_reauthenticate(current_password);

				const resp = await user_model.updatePassword(encryptedWithKeyName);
				if (resp.res === "err") {
					return Promise.reject(resp.err_msg);
				}

				return true;
			} catch (e) {
				console.error("password change error %o", e);
				if (e.toString().includes("invalid-login-credentials")) {
					return Promise.reject("The current password provided is incorrect.");
				} else if (
					[
						"Code=17026",
						"The given password is invalid. [ Password should be at least 6 characters ]",
					].includes(e.message)
				) {
					return Promise.reject("The new password is too short.");
				} else {
					return Promise.reject(e?.err_msg || e?.message || "An error occurred while updating the password.");
				}
			}
		},
	});

	const onSubmit = async e => {
		e.preventDefault();
		await updatePassword(pick(state, ["password", "current_password"]));
	};

	return (
		<Container>
			<Helmet>
				<title>Reset Password</title>
			</Helmet>
			<Segment>
				<Header>Change Your Password</Header>
				<Form loading={loading} error={error} success={success} onSubmit={onSubmit}>
					<Message error header="Error" content={error} />
					<Message success="true" header="Success" content={"Your password has been updated"} />
					<Form.Field className="!mb-5">
						<Form.Input
							type={passwordVisible ? "text" : "password"}
							name="current_password"
							placeholder="Enter your current password"
							label="Current Password"
							value={state.current_password}
							onChange={(e, data) => onChange(e, data)}
						/>
						<button
							onClick={event => {
								event.preventDefault();
								setPasswordVisible(!passwordVisible);
							}}
							className="absolute right-0 top-2 cursor-pointer z-10"
						>
							<SIcon
								name={passwordVisible ? "eye" : "eye"}
								size="sm"
								svg_cls=""
								cls="text-is2-gray-150 pr-3 mr-2"
								bold
							/>
						</button>
					</Form.Field>
					<Form.Field className="!mb-5">
						<Form.Input
							type={confirmPasswordVisible ? "text" : "password"}
							name="password"
							placeholder="Enter your new password"
							label="New Password"
							value={state.password}
							onChange={(e, data) => onChange(e, data)}
						/>
						<button
							onClick={e => {
								e.preventDefault();
								setConfirmPasswordVisible(!confirmPasswordVisible);
							}}
							className="absolute right-0 top-2 cursor-pointer z-10"
						>
							<SIcon
								name={confirmPasswordVisible ? "eye" : "eye"}
								size="sm"
								svg_cls=""
								cls="text-is2-gray-150 pr-3 mr-2"
								bold
							/>
						</button>
					</Form.Field>
					{state.password !== "" && <PasswordChecklist password={state.password} />}
					<Button color="green">Update Password</Button>
				</Form>
			</Segment>
		</Container>
	);
};

export default Password;
